import * as React from 'react';

import { CommonContextProvider } from '../features/common/contexts/common-context';
import {
  PicButton,
  PicRowField,
  PicText,
  PicNotifier,
  PicLink,
  PicRouter,
  PicFontSize,
  PicFontWeight,
  PicColor,
  PicCheckbox,
  PicDropdown,
  PicTagsInput,
  PicImageUpload,
  PicImage,
  PicNumberInput,
  PicField,
} from '@cardinalblue/pic-collage-cms-ui';
import { TemplateApi } from '../features/templates/apis/template-api';
import SingleTemplateContentSection from '../features/templates/components/single-template-contents';
import MultiTemplateContentSection from '../features/templates/components/multi-template-contents';
import { getTemplateTagNameById, getVipTemplateTagName } from '../features/common/utils/template-tag-helpers';
import { TemplateModel } from '../features/common/models/template-model';
import { getImageDimension, getWebpFrameCount } from '../features/common/utils/image';
import { SlotPathRenderMode } from '../features/common/models/slot-path-render-mode';
import { JsonDiffView } from '../features/templates/components/json-diff-view';
import { TemplateTagModel } from '../features/common/models/template-tag-model';
import { TemplateTagApi } from '../features/template-tags/apis/template-tag-api';

type Props = {
  formAuthenticityToken: string;
  templateId: number;
  creatorIds: string[];
  allTemplateLabelNames: string[];
};

const EditTemplatePageContent: React.FC<Props> = ({
  formAuthenticityToken,
  templateId,
  creatorIds,
  allTemplateLabelNames,
}) => {
  const picNotifier = React.useMemo(() => new PicNotifier(), []);
  const picRouter = React.useMemo(() => new PicRouter(), []);

  const templateApi = React.useMemo(() => TemplateApi.create(), []);
  const templateTagApi = React.useMemo(() => TemplateTagApi.create(), []);
  const [templateTags, setTemplateTags] = React.useState<TemplateTagModel[] | null>(null);
  const fetchTemplateTags = React.useCallback(async () => {
    const [error, returnedTemplateTags] = await templateTagApi.fetchAllTemplateTags();
    if (error) {
      picNotifier.notify({
        type: 'error',
        message: error.message,
      });
      return;
    }
    setTemplateTags(returnedTemplateTags);
  }, [templateTagApi, picNotifier]);

  React.useEffect(() => {
    fetchTemplateTags();
  }, [fetchTemplateTags]);
  const [template, setTemplate] = React.useState<TemplateModel | null>(null);
  const [draftTemplate, setDraftTemplate] = React.useState<TemplateModel | null>(null);

  const fetchTemplate = React.useCallback(async (): Promise<Error | null> => {
    const [error, returnedTemplate] = await templateApi.fetchTemplatebyId(templateId);
    if (error) {
      picNotifier.notify({
        type: 'error',
        message: error.message,
      });
      return error;
    }
    setTemplate(returnedTemplate);
    return null;
  }, [templateApi, picNotifier]);

  React.useEffect(() => {
    fetchTemplate();
  }, [fetchTemplate]);

  const resetDraftTemplate = React.useCallback(() => {
    setDraftTemplate(template ? template.clone() : null);
  }, [template]);

  React.useEffect(() => {
    resetDraftTemplate();
  }, [resetDraftTemplate]);

  const templateCreatorIdsGroups = React.useMemo(() => {
    if (!creatorIds) return [];
    const uniqueCreatorIds = Array.from(new Set(creatorIds));
    return [{ title: 'Creator Ids', items: uniqueCreatorIds }];
  }, [creatorIds]);

  const templateLabelNames = React.useMemo(() => {
    return allTemplateLabelNames;
  }, [allTemplateLabelNames]);

  const minimumSuitablePhotosCount = React.useMemo(() => {
    return 0;
  }, []);

  const maximumSuitablePhotosCount = React.useMemo(() => {
    return 100;
  }, []);

  const animatedThumbnailAcceptableMimeTypes = React.useMemo(() => {
    return ['image/webp'];
  }, []);

  const goToSourceUrl = React.useCallback(() => {
    if (template?.getSourceUrl()) {
      picRouter.open(template.getSourceUrl());
    }
  }, [template]);

  const handleTemplateIsAdjustableChange = React.useCallback(() => {
    if (!draftTemplate) return;
    setDraftTemplate((prevTemplate) => {
      if (!prevTemplate) return null;
      const updatedTemplate = prevTemplate.clone();
      updatedTemplate.updateIsAdjustable(!draftTemplate.getIsAdjustable());
      return updatedTemplate;
    });
  }, [draftTemplate]);

  const handleTemplateCreatorIdChange = React.useCallback((newCreatorId: string) => {
    setDraftTemplate((prevTemplate) => {
      if (!prevTemplate) return null;
      if (!prevTemplate.isSingleTemplateModel()) return prevTemplate;
      const updatedTemplate = prevTemplate.clone();
      updatedTemplate.updateCreatorId(newCreatorId);
      return updatedTemplate;
    });
  }, []);

  const handleTemplateCreatorIdRemove = React.useCallback(() => {
    setDraftTemplate((prevTemplate) => {
      if (!prevTemplate) return null;
      if (!prevTemplate.isSingleTemplateModel()) return prevTemplate;
      const updatedTemplate = prevTemplate.clone();
      updatedTemplate.updateCreatorId(null);
      return updatedTemplate;
    });
  }, []);

  const handleTemplateLowerSuitablePhotosCountChange = React.useCallback(
    (newLowerSuitablePhotosCount: number | null) => {
      if (!draftTemplate) return;
      if (newLowerSuitablePhotosCount === null) return;
      setDraftTemplate((prevTemplate) => {
        if (!prevTemplate) return null;
        const updatedTemplate = prevTemplate.clone();
        updatedTemplate.updateSuitablePhotosCount(
          prevTemplate.getSuitablePhotosCount().updateLower(newLowerSuitablePhotosCount),
        );
        return updatedTemplate;
      });
    },
    [draftTemplate],
  );

  const handleTemplateUpperSuitablePhotosCountChange = React.useCallback(
    (newUpperSuitablePhotosCount: number | null) => {
      if (!draftTemplate) return;
      setDraftTemplate((prevTemplate) => {
        if (!prevTemplate) return null;
        const updatedTemplate = prevTemplate.clone();
        updatedTemplate.updateSuitablePhotosCount(
          prevTemplate.getSuitablePhotosCount().updateUpper(newUpperSuitablePhotosCount),
        );
        return updatedTemplate;
      });
    },
    [draftTemplate],
  );

  const handleTemplateCategoriesChange = React.useCallback(
    (templateTagNames: string[]) => {
      setDraftTemplate((prevTemplate) => {
        if (!prevTemplate || !templateTags) return null;

        const updatedTemplate = prevTemplate.clone();
        const updatedTemplateTagNames = templateTagNames.filter((templateTagName) =>
          templateTags.some((tag) => tag.getName() === templateTagName),
        );
        const newTemplateTagNames = templateTagNames.filter(
          (templateTagName) => !templateTags.some((tag) => tag.getName() === templateTagName),
        );
        updatedTemplate.updateTemplateTagIds(
          updatedTemplateTagNames.map(
            (category) => templateTags.find((tag) => tag.getName() === category)?.getId() ?? 0,
          ),
        );
        updatedTemplate.updateNewTemplateTagNames(newTemplateTagNames);
        return updatedTemplate;
      });
    },
    [templateTags],
  );

  const handleTemplateLabelsChange = React.useCallback((labels: string[]) => {
    setDraftTemplate((prevTemplate) => {
      if (!prevTemplate) return null;
      const updatedTemplate = prevTemplate.clone();
      updatedTemplate.updateLabels(labels);
      return updatedTemplate;
    });
  }, []);

  const handleTemplateShouldRemoveAnimatedThumbnailChange = React.useCallback(() => {
    if (!draftTemplate) return;
    setDraftTemplate((prevTemplate) => {
      if (!prevTemplate) return null;
      const updatedTemplate = prevTemplate.clone();
      updatedTemplate.updateShouldRemoveAnimatedThumbnail(!draftTemplate.getShouldRemoveAnimatedThumbnail());
      return updatedTemplate;
    });
  }, [draftTemplate]);

  const handleTemplateThumbnailBinaryChange = React.useCallback((image: Uint8Array) => {
    setDraftTemplate((prevTemplate) => {
      if (!prevTemplate) return null;
      const updatedTemplate = prevTemplate.clone();
      updatedTemplate.updateThumbnailBinary(image);
      return updatedTemplate;
    });
  }, []);

  const handleTemplateThumbnailBinaryRemove = React.useCallback(() => {
    setDraftTemplate((prevTemplate) => {
      if (!prevTemplate) return null;
      const updatedTemplate = prevTemplate.clone();
      updatedTemplate.updateThumbnailBinary(null);
      return updatedTemplate;
    });
  }, []);

  const [animatedThumbnailFrameCountAlert, setAnimatedThumbnailFrameCountAlert] = React.useState<string | null>(null);
  const [animatedThumbnailDimensionAlert, setAnimatedThumbnailDimensionAlert] = React.useState<string | null>(null);
  const handleTemplateAnimatedThumbnailBinaryChange = React.useCallback((animatedThumbnail: Uint8Array) => {
    setDraftTemplate((prevTemplate) => {
      setAnimatedThumbnailFrameCountAlert(null);
      setAnimatedThumbnailDimensionAlert(null);
      if (!prevTemplate) return null;

      const frameCount = getWebpFrameCount(animatedThumbnail);
      if (frameCount !== null && frameCount > 15) {
        setAnimatedThumbnailFrameCountAlert(
          `⚠️ Please note that the frame count should be less than 15, frame count for this image: ${frameCount}`,
        );
      }

      getImageDimension(animatedThumbnail).then((imageDimension) => {
        if (imageDimension.height > 480 || imageDimension.width > 480) {
          setAnimatedThumbnailDimensionAlert(
            `⚠️ Please note that the width and height should not exceed 480px, dimension for this image: ${imageDimension.width}x${imageDimension.height}`,
          );
        }
      });

      const updatedTemplate = prevTemplate.clone();
      updatedTemplate.updateAnimatedThumbnailBinary(animatedThumbnail);
      return updatedTemplate;
    });
  }, []);

  const handleTemplateAnimatedThumbnailBinaryRemove = React.useCallback(() => {
    setDraftTemplate((prevTemplate) => {
      if (!prevTemplate) return null;
      const updatedTemplate = prevTemplate.clone();
      updatedTemplate.updateAnimatedThumbnailBinary(null);
      return updatedTemplate;
    });
  }, [draftTemplate]);

  const handleTemplateStickerScrapInfosChange = React.useCallback((stickerId: string, newIsFrozen: boolean) => {
    setDraftTemplate((prevTemplate) => {
      if (!prevTemplate || !prevTemplate.isSingleTemplateModel()) return null;
      const updatedTemplate = prevTemplate.clone();
      updatedTemplate.updateStickerScrapInfo(stickerId, newIsFrozen);
      return updatedTemplate;
    });
  }, []);

  const handleSlotPathRenderModeChange = React.useCallback(
    (slotIndex: number, newPathRenderMode: SlotPathRenderMode | null) => {
      setDraftTemplate((prevTemplate) => {
        if (!prevTemplate || !prevTemplate.isSingleTemplateModel()) return null;
        const updatedTemplate = prevTemplate.clone();
        updatedTemplate.updateSlotPathRenderMode(slotIndex, newPathRenderMode);
        return updatedTemplate;
      });
    },
    [],
  );

  const [isSavingTemplate, setIsSavingTemplate] = React.useState(false);
  const handleTemplateSaveClick = React.useCallback(async () => {
    if (!draftTemplate || !template || !templateTags) {
      return;
    }

    setIsSavingTemplate(true);
    const error = await templateApi.updateTemplate(formAuthenticityToken, template, draftTemplate, templateTags);
    if (error) {
      picNotifier.notify({
        type: 'error',
        message: error.message,
      });
      setIsSavingTemplate(false);
      return;
    }
    picRouter.redirect(`/templates/${templateId}`, {
      type: 'success',
      message: 'Template updated successfully',
    });
  }, [template, templateTags, draftTemplate, templateApi, picNotifier, picRouter, templateId]);

  const handlePageIdsUpdate = React.useCallback((templatePageId: number[]) => {
    setDraftTemplate((prev) => {
      if (!prev) return prev;
      if (!prev.isMultiTemplateModel()) return prev;

      const updatedTemplate = prev.clone();
      updatedTemplate.updateTemplatePageIds(templatePageId);
      return updatedTemplate;
    });
  }, []);

  const handleTemplateSuitsVideoChange = React.useCallback((suitsVideo: boolean) => {
    setDraftTemplate((prev) => {
      if (!prev) return prev;
      const updatedTemplate = prev.clone();
      updatedTemplate.updateSuitsVideo(suitsVideo);
      return updatedTemplate;
    });
  }, []);

  const thumbnailSectionWidth = React.useMemo(() => {
    return '400px';
  }, []);

  const [editSectionLeft, setEditSectionLeft] = React.useState(0);
  const [editSectionRef, setEditSectionRef] = React.useState<HTMLElement | null>(null);
  React.useEffect(() => {
    if (!editSectionRef) {
      return () => {};
    }

    const updateEditSectionLeft = () => {
      setEditSectionLeft(editSectionRef.offsetLeft);
    };
    updateEditSectionLeft();

    window.addEventListener('resize', updateEditSectionLeft);

    return () => {
      window.removeEventListener('resize', updateEditSectionLeft);
    };
  }, [editSectionRef]);

  const selectedExistingTemplateTagNames = React.useMemo(() => {
    if (!templateTags || !draftTemplate) return [];

    return draftTemplate.getTemplateTagIds().map((tagId) => getTemplateTagNameById(tagId, templateTags)) ?? [];
  }, [draftTemplate, templateTags]);

  const newlyAddedTemplateTagNames = React.useMemo(() => {
    if (!draftTemplate) return [];

    return draftTemplate.getNewTemplateTagNames() ?? [];
  }, [draftTemplate]);

  const inputTemplateTagNames = React.useMemo(() => {
    return selectedExistingTemplateTagNames.concat(newlyAddedTemplateTagNames);
  }, [selectedExistingTemplateTagNames, newlyAddedTemplateTagNames]);

  const templateTagNames = React.useMemo(() => {
    if (!templateTags) return [];
    return templateTags.map((tag) => tag.getName());
  }, [templateTags]);

  const vipTemplateTagName = React.useMemo(() => {
    if (!templateTags) return null;

    return getVipTemplateTagName(templateTags);
  }, [templateTags]);

  return (
    <div>
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: '80px',
          display: 'flex',
          padding: '0 20px',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: 'white',
          borderTop: `1px solid ${PicColor.Grey200}`,
          zIndex: 3,
        }}
      >
        <PicText size={PicFontSize.Sm} weight={PicFontWeight.Medium}>
          Edit Template
        </PicText>
        <div>
          <PicButton
            copy={isSavingTemplate ? 'Saving...' : 'Save'}
            disabled={isSavingTemplate}
            onClick={handleTemplateSaveClick}
            type="primary"
          />
        </div>
      </div>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: editSectionLeft,
          width: thumbnailSectionWidth,
          height: '100%',
          display: 'flex',
          flexFlow: 'column',
          overflow: 'auto',
          gap: '12px',
          padding: '60px 20px 80px',
          zIndex: 2,
        }}
      >
        {draftTemplate && (
          <PicField label="Thumbnail">
            <PicImage src={draftTemplate.getDisplayThumbnailUrl()} width="100%" />
          </PicField>
        )}
        {draftTemplate && draftTemplate.getDisplayAnimatedThumbnailUrl() && (
          <PicField label="Animated Thumbnail">
            <PicImage src={draftTemplate.getDisplayAnimatedThumbnailUrl()} width="100%" />
          </PicField>
        )}
      </div>
      <div
        ref={(ref) => {
          setEditSectionRef(ref);
        }}
        style={{
          position: 'relative',
          margin: '0 auto',
          padding: `20px 4px 80px ${thumbnailSectionWidth}`,
          display: 'flex',
          flexFlow: 'column',
          gap: '12px',
        }}
      >
        <div style={{ display: 'flex', flexFlow: 'column', gap: '12px' }}>
          {draftTemplate && (
            <div style={{ display: 'flex', flexFlow: 'column', gap: '12px' }}>
              <PicRowField label="id" labelWidth="36%">
                <PicText>{draftTemplate.getId()}</PicText>
              </PicRowField>
              <PicRowField label="author" labelWidth="36%">
                <PicText>{draftTemplate.getAuthor()}</PicText>
              </PicRowField>
              <PicRowField label="uploader_info" labelWidth="36%">
                <PicText>{draftTemplate.getUploaderInfo()}</PicText>
              </PicRowField>
              <PicRowField label="source url" labelWidth="36%">
                <PicLink copy={draftTemplate.getSourceUrl() ?? ''} onClick={goToSourceUrl} />
              </PicRowField>
              <PicRowField label="created at" labelWidth="36%">
                <PicText>{draftTemplate.getCreatedAt().toTimeString()}</PicText>
              </PicRowField>
              <PicRowField label="updated at" labelWidth="36%">
                <PicText>{draftTemplate.getUpdatedAt().toTimeString()}</PicText>
              </PicRowField>
              <PicRowField label="adjustable?" labelWidth="36%">
                <PicCheckbox checked={draftTemplate.getIsAdjustable()} onChange={handleTemplateIsAdjustableChange} />
              </PicRowField>
              {draftTemplate.isSingleTemplateModel() && (
                <PicRowField label="creator id" labelWidth="36%">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '300px' }}>
                      <PicDropdown
                        value={draftTemplate.getCreatorId()}
                        placeholder="Select creator id"
                        itemGroups={templateCreatorIdsGroups}
                        enableFilter
                        itemLabelGetter={(item) => item}
                        itemValueGetter={(item) => item}
                        onSelect={handleTemplateCreatorIdChange}
                      />
                    </div>
                    <PicLink copy="Clear" onClick={handleTemplateCreatorIdRemove} />
                  </div>
                </PicRowField>
              )}
              <PicRowField label="suitable photos count" labelWidth="36%" verticalAlign="center">
                <div style={{ display: 'flex', flexFlow: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', flexFlow: 'row', gap: '12px', alignItems: 'center' }}>
                    <div>
                      <PicRowField label="Lower" labelWidth="30px">
                        <div style={{ display: 'flex', flexFlow: 'row', gap: '4px', alignItems: 'center' }}>
                          <PicNumberInput
                            value={draftTemplate.getSuitablePhotosCount().getLower()}
                            step={1}
                            min={minimumSuitablePhotosCount}
                            max={maximumSuitablePhotosCount}
                            onInput={handleTemplateLowerSuitablePhotosCountChange}
                          />
                        </div>
                      </PicRowField>
                    </div>
                    <div>
                      <PicRowField label="Upper" labelWidth="30px">
                        <div style={{ display: 'flex', flexFlow: 'row', gap: '4px', alignItems: 'center' }}>
                          {draftTemplate.getSuitablePhotosCount().getUpper() !== null && (
                            <PicNumberInput
                              value={draftTemplate.getSuitablePhotosCount().getUpper()}
                              step={1}
                              min={minimumSuitablePhotosCount}
                              max={maximumSuitablePhotosCount}
                              onInput={handleTemplateUpperSuitablePhotosCountChange}
                            />
                          )}
                          {draftTemplate.getSuitablePhotosCount().getUpper() === null && (
                            <PicLink
                              copy="Add Limit"
                              onClick={() => {
                                handleTemplateUpperSuitablePhotosCountChange(
                                  draftTemplate.getSuitablePhotosCount().getLower(),
                                );
                              }}
                            />
                          )}
                          {draftTemplate.getSuitablePhotosCount().getUpper() !== null && (
                            <PicLink
                              copy="Remove Limit"
                              onClick={() => {
                                handleTemplateUpperSuitablePhotosCountChange(null);
                              }}
                            />
                          )}
                        </div>
                      </PicRowField>
                    </div>
                  </div>
                </div>
              </PicRowField>
              <PicRowField label="suitable photos count preview" labelWidth="36%" verticalAlign="center">
                <PicText>{draftTemplate.getSuitablePhotosCount().getFormatted()}</PicText>
              </PicRowField>
              <PicRowField label="suits video" labelWidth="36%">
                <PicCheckbox checked={draftTemplate.getSuitsVideo()} onChange={handleTemplateSuitsVideoChange} />
              </PicRowField>
              <PicRowField label="categories" labelWidth="36%">
                <div style={{ display: 'flex', gap: '2px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <PicTagsInput
                    value={inputTemplateTagNames}
                    suggestions={templateTagNames}
                    highlightedTags={vipTemplateTagName ? [vipTemplateTagName] : undefined}
                    onInput={handleTemplateCategoriesChange}
                  />
                </div>
              </PicRowField>
              <PicRowField label="labels" labelWidth="36%">
                <div style={{ display: 'flex', gap: '2px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <PicTagsInput
                    value={draftTemplate.getLabels()}
                    suggestions={templateLabelNames}
                    onInput={handleTemplateLabelsChange}
                  />
                </div>
              </PicRowField>
              <PicRowField label="thumbnail" labelWidth="36%">
                <div style={{ display: 'flex', flexFlow: 'row', gap: '12px', alignItems: 'center' }}>
                  <PicImageUpload
                    accept=""
                    onUpload={(image) => {
                      image.arrayBuffer().then((imageArrayBuffer) => {
                        handleTemplateThumbnailBinaryChange(new Uint8Array(imageArrayBuffer));
                      });
                    }}
                  />
                  <PicLink copy="Reset" onClick={() => handleTemplateThumbnailBinaryRemove()} />
                </div>
              </PicRowField>

              <PicRowField label="animated thumbnail (webp)" labelWidth="36%" verticalAlign="top">
                <div style={{ display: 'flex', flexFlow: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', flexFlow: 'row', gap: '12px', alignItems: 'center' }}>
                    <PicImageUpload
                      accept={animatedThumbnailAcceptableMimeTypes.join(',')}
                      onUpload={(image) => {
                        image.arrayBuffer().then((imageArrayBuffer) => {
                          handleTemplateAnimatedThumbnailBinaryChange(new Uint8Array(imageArrayBuffer));
                        });
                      }}
                    />
                    <PicLink copy="Reset" onClick={() => handleTemplateAnimatedThumbnailBinaryRemove()} />
                  </div>
                  <div style={{ display: 'flex', flexFlow: 'column', gap: '4px' }}>
                    {animatedThumbnailFrameCountAlert && (
                      <PicText size={PicFontSize.Sm} weight={PicFontWeight.Medium} color={PicColor.Red500}>
                        {animatedThumbnailFrameCountAlert}
                      </PicText>
                    )}
                    {animatedThumbnailDimensionAlert && (
                      <PicText size={PicFontSize.Sm} weight={PicFontWeight.Medium} color={PicColor.Red500}>
                        {animatedThumbnailDimensionAlert}
                      </PicText>
                    )}
                  </div>
                </div>
              </PicRowField>
              <PicRowField label="should remove animated thumbnail" labelWidth="36%">
                <PicCheckbox
                  checked={draftTemplate.getShouldRemoveAnimatedThumbnail()}
                  disabled={!draftTemplate.getAnimatedThumbnailUrl()}
                  onChange={handleTemplateShouldRemoveAnimatedThumbnailChange}
                />
              </PicRowField>
              <PicRowField label="minimal version iOS" labelWidth="36%">
                <PicText>{draftTemplate.getMinimalRequiredVersionIos()?.toString()}</PicText>
              </PicRowField>
              <PicRowField label="minimal version Android" labelWidth="36%">
                <PicText>{draftTemplate.getMinimalRequiredVersionAndroid()?.toString()}</PicText>
              </PicRowField>
              {draftTemplate.isMultiTemplateModel() && (
                <PicRowField label="pages (template ids)" labelWidth="36%">
                  <PicTagsInput
                    value={draftTemplate.getTemplatePageIds().map((templatePageId) => `${templatePageId}`)}
                    onInput={(templatePageIds) => {
                      handlePageIdsUpdate(templatePageIds.map((templatePageId) => parseInt(templatePageId, 10)));
                    }}
                  />
                </PicRowField>
              )}
            </div>
          )}
        </div>
        <div>
          {draftTemplate?.isSingleTemplateModel() && (
            <SingleTemplateContentSection
              template={draftTemplate}
              isEditingStickersMovableStatus={true}
              onStickerScrapIsFrozenChange={handleTemplateStickerScrapInfosChange}
              onSlotPathRenderModeChange={handleSlotPathRenderModeChange}
            />
          )}
        </div>
        <div>{draftTemplate?.isMultiTemplateModel() && <MultiTemplateContentSection template={draftTemplate} />}</div>
        <div style={{ flexGrow: 1 }}>
          {draftTemplate && draftTemplate.isSingleTemplateModel() && template?.isSingleTemplateModel() && (
            <div style={{ display: 'flex', flexFlow: 'column', gap: '24px' }}>
              <PicField label="Collage Structure">
                <JsonDiffView oldJson={template.getCollageStruct()} newJson={draftTemplate.getCollageStruct()} />
              </PicField>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const EditTemplatePage: React.FC<Props> = (props) => {
  return (
    <CommonContextProvider>
      <EditTemplatePageContent {...props} />
    </CommonContextProvider>
  );
};

export default EditTemplatePage;
